import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUser } from "@clerk/clerk-react";
import {
  Box, Typography, Paper, Stack, Button, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, Chip, Select, MenuItem,
  FormControl, InputLabel, useTheme, Divider, IconButton, Tab, Tabs,
  Alert, Avatar, alpha, Tooltip, Grid, Autocomplete, Slide
} from "@mui/material";
import {
  AddOutlined as AddIcon,
  CalendarMonthOutlined as CalendarIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  AccessTimeOutlined as TimeIcon,
  CurrencyRupeeOutlined as RupeeIcon,
  ConfirmationNumberOutlined as TicketIcon,
  FiberManualRecord as DotIcon,
  InfoOutlined as InfoIcon,
  VisibilityOutlined as ViewIcon,
  ToggleOn as ActiveIcon,
  ToggleOff as InactiveIcon
} from "@mui/icons-material";
import { DatePicker, TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { getPartnerShows, createPartnerShow, updatePartnerShow, deletePartnerShow, getPartnerMovies } from "../../store/slices/showSlice";
import { getPartnerScreens } from "../../store/slices/screenSlice";
import { useSocket } from "../../contexts/SocketContext";
import { toast } from "react-toastify";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PartnerShows = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const { user: clerkUser } = useUser();
  const { socket: socketInstance } = useSocket();
  const { shows, movies, loading } = useSelector(s => s.show);
  const { screens } = useSelector(s => s.screen);
  const { partner } = useSelector(s => s.partner);
  const isDark = mode === 'dark';

  const colors = useMemo(() => ({
    primary: "#666CFF",
    success: "#72E128",
    warning: "#FDB528",
    error: "#FF4D49",
    info: "#26C6F9",
    textPrimary: isDark ? "rgba(231, 227, 252, 0.87)" : "rgba(58, 53, 65, 0.87)",
    textSecondary: isDark ? "rgba(231, 227, 252, 0.6)" : "rgba(58, 53, 65, 0.6)",
    background: isDark ? "#282A42" : "#ffffff",
    paper: isDark ? "#30334E" : "#ffffff",
    divider: isDark ? "rgba(231, 227, 252, 0.12)" : "rgba(58, 53, 65, 0.12)",
  }), [isDark]);

  const [dialog, setDialog] = useState(false);
  const [editShow, setEditShow] = useState(null);
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState({
    movie: '', screenNumber: 1, showDate: '', showTime: '',
    screenType: 'Standard', language: 'Hindi',
    pricing: { silver: 150, gold: 250, premium: 400 }
  });

  const fetchShows = React.useCallback(() => {
    if (clerkUser?.id) {
      const theaterId = localStorage.getItem('activeTheaterId');
      dispatch(getPartnerShows({ userId: clerkUser.id, theaterId }));
      dispatch(getPartnerScreens({ userId: clerkUser.id, theaterId }));
      dispatch(getPartnerMovies());
    }
  }, [dispatch, clerkUser?.id]);

  useEffect(() => {
    fetchShows();
  }, [fetchShows]);

  useEffect(() => {
    if (socketInstance) {
      const events = ["show-added", "show-updated", "show-deleted", "partner-updated"];
      events.forEach(e => socketInstance.on(e, fetchShows));
      return () => events.forEach(e => socketInstance.off(e, fetchShows));
    }
  }, [socketInstance, fetchShows]);

  const openCreate = () => {
    setEditShow(null);
    setForm({ movie: '', screenNumber: screens[0]?.screenNumber || 1, showDate: '', showTime: '', screenType: 'Standard', language: 'Hindi', pricing: { silver: 150, gold: 250, premium: 400 } });
    setDialog(true);
  };

  const openEdit = (show) => {
    setEditShow(show);
    setForm({
      movie: show.movie?._id || show.movie,
      screenNumber: show.screenNumber,
      showDate: show.showDate ? new Date(show.showDate).toISOString().split('T')[0] : '',
      showTime: show.showTime || '',
      screenType: show.screenType || 'Standard',
      language: show.language || 'Hindi',
      pricing: show.pricing || { silver: show.price || 150, gold: 250, premium: 400 }
    });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.movie || !form.showDate || !form.showTime) return toast.error("Fill all required fields");
    try {
      if (editShow) {
        await dispatch(updatePartnerShow({
          userId: clerkUser.id, showId: editShow._id,
          data: { showDate: form.showDate, showTime: form.showTime, screenNumber: form.screenNumber, pricing: form.pricing, price: form.pricing.silver, isActive: true }
        })).unwrap();
        toast.success("Show updated");
      } else {
        const theaterId = localStorage.getItem('activeTheaterId');
        await dispatch(createPartnerShow({
          userId: clerkUser.id,
          showData: {
            movie: form.movie,
            screenNumber: form.screenNumber,
            showDate: form.showDate,
            showTime: form.showTime,
            screenType: form.screenType,
            price: form.pricing.silver,
            pricing: form.pricing,
            isActive: true,
            theaterId
          }
        })).unwrap();
        toast.success("Show created");
      }
      setDialog(false);
    } catch (err) { toast.error(err.message || "Failed"); }
  };

  const handleDelete = async (showId) => {
    if (!window.confirm("Delete this show?")) return;
    try {
      await dispatch(deletePartnerShow({ userId: clerkUser.id, showId })).unwrap();
      toast.success("Show deleted");
    } catch (err) { toast.error(err.message || err?.response?.data?.message || "Cannot delete"); }
  };

  const handleToggle = async (show) => {
    try {
      await dispatch(updatePartnerShow({
        userId: clerkUser.id, showId: show._id,
        data: { isActive: !show.isActive }
      })).unwrap();
      toast.success(show.isActive ? "Show deactivated" : "Show activated");
    } catch (err) { toast.error("Failed"); }
  };

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  const upcoming = useMemo(() => shows.filter(s => new Date(s.showDate) >= new Date(today)), [shows, today]);
  const past = useMemo(() => shows.filter(s => new Date(s.showDate) < new Date(today)), [shows, today]);
  const display = useMemo(() => tab === 0 ? upcoming : past, [tab, upcoming, past]);

  const statusColor = useMemo(() => (s) =>
    !s.isActive ? colors.error : s.availableSeats <= 0 ? colors.warning : new Date(s.showDate) >= new Date(today) ? colors.success : colors.textSecondary,
    [colors, today]);

  const statusLabel = useMemo(() => (s) =>
    !s.isActive ? 'INACTIVE' : s.availableSeats <= 0 ? 'SOLD OUT' : new Date(s.showDate) >= new Date(today) ? 'ACTIVE' : 'EXPIRED',
    [today]);

  const columns = useMemo(() => [
    {
      accessorKey: 'movie.title',
      header: 'Production',
      Cell: ({ row }) => (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar variant="rounded" src={row.original.movie?.poster} sx={{ width: 32, height: 48, borderRadius: '4px' }}>
            <TicketIcon />
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '0.875rem' }}>{row.original.movie?.title}</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: colors.textSecondary }}>{row.original.language} | Node #{row.original.screenNumber}</Typography>
          </Box>
        </Stack>
      ),
    },
    {
      accessorKey: 'showDate',
      header: 'Temporal Slot',
      Cell: ({ row }) => (
        <Box>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{new Date(row.original.showDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: colors.primary, fontWeight: 600 }}>{row.original.showTime}</Typography>
        </Box>
      ),
    },
    {
      accessorKey: 'availableSeats',
      header: 'Load Units',
      Cell: ({ row }) => (
        <Box>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 800 }}>{row.original.availableSeats} / {row.original.totalSeats}</Typography>
          <Typography sx={{ fontSize: '0.7rem', color: colors.textSecondary }}>Remaining</Typography>
        </Box>
      ),
    },
    {
      accessorKey: 'price',
      header: 'Valuation',
      Cell: ({ renderedCellValue }) => (
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 900, color: colors.success }}>₹{renderedCellValue}</Typography>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status Protocol',
      Cell: ({ row }) => (
        <Chip
          label={statusLabel(row.original)}
          size="small"
          sx={{ height: 22, fontSize: '0.62rem', fontWeight: 900, bgcolor: alpha(statusColor(row.original), 0.1), color: statusColor(row.original) }}
        />
      ),
    },
    {
      id: 'actions',
      header: 'Action Hub',
      Cell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Audit Slot">
            <IconButton size="small" onClick={() => openEdit(row.original)} sx={{ color: colors.primary, bgcolor: alpha(colors.primary, 0.08) }}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.original.isActive ? "Deactivate" : "Activate"}>
            <IconButton size="small" onClick={() => handleToggle(row.original)} sx={{ color: row.original.isActive ? colors.warning : colors.success, bgcolor: alpha(row.original.isActive ? colors.warning : colors.success, 0.08) }}>
              {row.original.isActive ? <InactiveIcon sx={{ fontSize: 16 }} /> : <ActiveIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Purge Node">
            <IconButton size="small" onClick={() => handleDelete(row.original._id)} sx={{ color: colors.error, bgcolor: alpha(colors.error, 0.08) }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ], [colors, statusLabel, statusColor]);

  const table = useMaterialReactTable({
    columns,
    data: display,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: true,
    enableSorting: true,
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: '12px',
        border: `1px solid ${colors.divider}`,
        bgcolor: colors.paper,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 800,
        fontSize: '0.7rem',
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        py: 2,
        bgcolor: alpha(colors.textPrimary, 0.02),
      },
    },
    muiTableBodyCellProps: {
      sx: {
        py: 1,
        color: colors.textPrimary,
        borderBottom: `1px solid ${colors.divider}`,
      },
    },
  });

  return (
    <Box sx={{ pb: 5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: colors.textPrimary, letterSpacing: '-0.02em', mb: 0.5 }}>
            Production Pipeline
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
            Orchestrate temporal slots and seat inventory for movie releases
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          disabled={partner?.kycStatus !== 'APPROVED'}
          sx={{ borderRadius: '10px', px: 3, py: 1, fontWeight: 700, bgcolor: colors.primary, '&:hover': { bgcolor: alpha(colors.primary, 0.8) } }}>
          Schedule New Slot
        </Button>
      </Box>

      {partner?.kycStatus !== 'APPROVED' && (
        <Alert icon={<InfoIcon />} severity="info" sx={{ mb: 4, borderRadius: '12px', fontWeight: 700, bgcolor: alpha(colors.info, 0.05), color: colors.info, border: `1px solid ${alpha(colors.info, 0.1)}` }}>
          PIPELINE LOCKED: Verification of partner credentials is required for scheduling.
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 0.5, mb: 3, bgcolor: alpha(colors.textPrimary, 0.05), border: 'none', borderRadius: '12px', width: 'fit-content' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 800, fontSize: '0.75rem', minHeight: 40, color: colors.textSecondary, borderRadius: '8px', transition: '0.2s', mx: 0.5 },
            '& .Mui-selected': { bgcolor: colors.background, color: `${colors.primary} !important`, boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.06)' },
            '& .MuiTabs-indicator': { display: 'none' }
          }}
        >
          <Tab label={`Upcoming Matrix (${upcoming.length})`} />
          <Tab label={`Historical Data (${past.length})`} />
        </Tabs>
      </Paper>

      <MaterialReactTable table={table} />

      <Dialog
        open={dialog}
        onClose={() => setDialog(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: '28px',
            bgcolor: isDark ? alpha('#0f1014', 0.95) : '#fff',
            backgroundImage: 'none',
            border: `1px solid ${alpha(colors.textPrimary, 0.08)}`,
            backdropFilter: 'blur(30px)',
            boxShadow: isDark ? '0 50px 100px rgba(0,0,0,0.8)' : '0 20px 60px rgba(0,0,0,0.08)'
          }
        }}
      >
        <DialogTitle sx={{ p: 4, pb: 2 }}>
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(colors.primary, 0.1), color: colors.primary, width: 56, height: 56, borderRadius: '18px', border: `1px solid ${alpha(colors.primary, 0.2)}` }}>
              <CalendarIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 950, fontSize: '1.5rem', color: colors.textPrimary, letterSpacing: '-0.03em', lineHeight: 1.2 }}>
                {editShow ? 'Matrix Update' : 'Slot Initialization'}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: colors.primary, letterSpacing: '0.12em', textTransform: 'uppercase', mt: 0.5 }}>
                Temporal Scheduling Protocol
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 4, pt: 1 }}>
          <Typography sx={{ color: colors.textSecondary, mb: 4, fontSize: '0.9rem', fontWeight: 500, lineHeight: 1.6, opacity: 0.8 }}>
            Synchronize high-fidelity temporal nodes with regional distribution matrices. All overrides require administrator-level clearance.
          </Typography>

          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={movies}
                  getOptionLabel={(option) => `${option.title} [${option.language.toUpperCase()}]`}
                  value={movies.find(m => m._id === form.movie) || null}
                  onChange={(_, newValue) => setForm(p => ({ ...p, movie: newValue?._id || '' }))}
                  disabled={!!editShow}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Production Unit *"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '16px',
                          bgcolor: alpha(colors.textPrimary, 0.02),
                          '& fieldset': { borderColor: alpha(colors.textPrimary, 0.1) },
                          '&:hover fieldset': { borderColor: colors.primary },
                          '&.Mui-focused fieldset': { borderWidth: '2px' }
                        },
                        '& .MuiInputLabel-root': { fontWeight: 700, fontSize: '0.85rem' }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Network Node</InputLabel>
                  <Select
                    value={form.screenNumber}
                    onChange={e => setForm(p => ({ ...p, screenNumber: e.target.value }))}
                    label="Network Node"
                    sx={{
                      borderRadius: '16px',
                      bgcolor: alpha(colors.textPrimary, 0.02),
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(colors.textPrimary, 0.1) },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: '2px' }
                    }}
                  >
                    {screens.map(s => <MenuItem key={s._id} value={s.screenNumber} sx={{ fontWeight: 600 }}>{s.name} [NODE #{s.screenNumber}]</MenuItem>)}
                    {screens.length === 0 && <MenuItem value={1}>Node Alpha (1)</MenuItem>}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Dialect Protocol</InputLabel>
                  <Select
                    value={form.language}
                    onChange={e => setForm(p => ({ ...p, language: e.target.value }))}
                    label="Dialect Protocol"
                    sx={{
                      borderRadius: '16px',
                      bgcolor: alpha(colors.textPrimary, 0.02),
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(colors.textPrimary, 0.1) },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: '2px' }
                    }}
                  >
                    {['Hindi', 'English', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Marathi', 'Bengali'].map(l => (
                      <MenuItem key={l} value={l} sx={{ fontWeight: 600 }}>{l}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 6 }}>
                <DatePicker
                  label="Execution Date *"
                  value={form.showDate ? moment(form.showDate) : null}
                  onChange={(val) => setForm(p => ({ ...p, showDate: val ? val.format('YYYY-MM-DD') : '' }))}
                  minDate={moment()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '16px',
                          bgcolor: alpha(colors.textPrimary, 0.02),
                          '& fieldset': { borderColor: alpha(colors.textPrimary, 0.1) },
                        },
                        '& .MuiInputLabel-root': { fontWeight: 700, fontSize: '0.85rem' }
                      }
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TimePicker
                  label="Temporal Offset *"
                  value={form.showTime ? moment(form.showTime, 'HH:mm') : null}
                  onChange={(val) => setForm(p => ({ ...p, showTime: val ? val.format('HH:mm') : '' }))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '16px',
                          bgcolor: alpha(colors.textPrimary, 0.02),
                          '& fieldset': { borderColor: alpha(colors.textPrimary, 0.1) },
                        },
                        '& .MuiInputLabel-root': { fontWeight: 700, fontSize: '0.85rem' }
                      }
                    }
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1, '&::before, &::after': { borderColor: alpha(colors.textPrimary, 0.1) } }}>
                  <Chip
                    label="VALUATION PARAMETERS (INR)"
                    size="small"
                    sx={{ fontWeight: 900, fontSize: '0.65rem', bgcolor: alpha(colors.warning, 0.1), color: colors.warning, letterSpacing: '0.12em', px: 1 }}
                  />
                </Divider>
              </Grid>

              {['silver', 'gold', 'premium'].map(tier => (
                <Grid size={{ xs: 4 }} key={tier}>
                  <TextField
                    fullWidth
                    type="number"
                    label={tier.toUpperCase()}
                    value={form.pricing[tier]}
                    onChange={e => setForm(p => ({ ...p, pricing: { ...p.pricing, [tier]: parseInt(e.target.value) || 0 } }))}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '16px',
                        bgcolor: alpha(colors.textPrimary, 0.02),
                        '& fieldset': { borderColor: alpha(colors.textPrimary, 0.1) },
                      },
                      '& .MuiInputLabel-root': { fontWeight: 800, fontSize: '0.75rem' }
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </LocalizationProvider>
        </DialogContent>

        <DialogActions sx={{ p: 4, pt: 1, justifyContent: 'space-between' }}>
          <Button
            onClick={() => setDialog(false)}
            sx={{ fontWeight: 800, color: colors.textSecondary, textTransform: 'none', px: 3, borderRadius: '12px', fontSize: '0.9rem' }}
          >
            Abort Sync
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              borderRadius: '16px',
              px: 5,
              py: 2,
              fontWeight: 900,
              textTransform: 'none',
              bgcolor: colors.primary,
              fontSize: '0.95rem',
              boxShadow: `0 15px 30px ${alpha(colors.primary, 0.3)}`,
              '&:hover': { bgcolor: alpha(colors.primary, 0.8), transform: 'translateY(-2px)' },
              transition: '0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {editShow ? 'Update Registry' : 'Commit Configuration'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerShows;
